import React, { useState, useEffect } from "react";
import ClientBackground from "./ClientBackground";
import { ChevronLeft, Share2 } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../api";
import LoadingSpinner from "./LoadingSpinner";

export default function ProgressTracking() {
  const navigate = useNavigate();
  const { childId } = useParams();
  const [progressData, setProgressData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProgressData = async () => {
      try {
        setLoading(true);
        const response = await api.get(
          `/children/profiles/${childId}/track_progress/`
        );
        setProgressData(response.data);
        setError(null);
      } catch (err) {
        setError("Failed to fetch progress data.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (childId) {
      fetchProgressData();
    }
  }, [childId]);

  if (loading) {
    return (
      <div className="relative min-h-screen">
        <ClientBackground />
        <div className="absolute inset-0 flex items-center justify-center">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="relative min-h-screen">
        <ClientBackground />
        <div className="absolute inset-0 flex items-center justify-center text-red-500">
          {error}
        </div>
      </div>
    );
  }

  if (!progressData) {
    return (
      <div className="relative min-h-screen">
        <ClientBackground />
        <div className="absolute inset-0 flex items-center justify-center text-gray-500">
          No progress data available.
        </div>
      </div>
    );
  }

  const {
    child_name: childName,
    last_updated: lastUpdated,
    progress_bars: progressBars,
    main_progress_components: mainProgressComponents,
    latest_achievements: latestAchievements,
    next_goals: nextGoals,
  } = progressData;

  // Calculate overall progress and prepare chart data
  let totalWeightedProgress = 0;
  let totalWeight = 0;
  let totalunits=0;
  const chartData = mainProgressComponents.map((component) => {
    const matchingBar = progressBars.find((bar) => bar.category === component.category);
    
    const completedMilestones = matchingBar ? matchingBar.completed_milestones : 0;
    const totalMilestones = matchingBar ? matchingBar.total_milestones : 0;
    
    const progressPercent =  (completedMilestones / totalMilestones) * 100 ;
    
    // Add to overall calculation
    totalWeightedProgress += progressPercent;
    totalunits+=1;
    console.log(`Component: ${component.name}, Completed: ${completedMilestones}, Total: ${totalMilestones}, Progress: ${progressPercent}%, Weight: ${component.percentage}% Total Weighted Progress: ${totalWeightedProgress}, Total Weight: ${totalWeight} ,units:${totalunits}`);
    
    return {
      name: component.name,
      color: component.color,
      weight: component.percentage,
      progress: progressPercent,
      completed: completedMilestones,
      total: totalMilestones
    };
  });

  // Calculate overall progress (weighted average)
  const overallProgress = totalunits > 0 ? totalWeightedProgress / totalunits : 0;

  return (
    <div className="relative min-h-screen">
      <ClientBackground />

      <div className="relative z-10 flex flex-col min-h-screen">
        {/* Fixed Header */}
        <div className="fixed top-0 left-0 right-0 z-50 bg-white/30 backdrop-blur-sm px-4 md:px-8 py-4 flex items-center justify-between shadow-sm">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2">
            <ChevronLeft className="w-6 h-6 text-gray-800" />
          </button>
          <div className="flex flex-col items-center">
            <h1 className="text-lg md:text-xl font-semibold text-[#1e3a5f]">
              Track Progress
            </h1>
            <p className="text-xs text-gray-500">
              Progress tracking for {childName}
            </p>
          </div>
          <div className="w-8" />
        </div>

        {/* Main Content with padding for fixed header/footer */}
        <div className="flex-1 px-4 md:px-6 lg:px-8 mt-12 pt-10 pb-28 overflow-y-auto">
          <div className="max-w-2xl mx-auto space-y-5">
            
            {/* Main Progress Dial Card */}
            <div className="rounded-2xl p-5 md:p-6 ">
              <div className="flex flex-col items-center">
                <SegmentedDonutChart
                  data={chartData}
                  overallProgress={overallProgress}
                />

                {/* Legend */}
                <div className="flex items-center justify-center gap-4 mt-8 mb-4 text-[10px] md:text-xs font-medium text-gray-600">
                  {chartData.map((item) => (
                    <div key={item.name} className="flex items-center gap-1">
                      <span
                        className="w-3 h-3 rounded-sm"
                        style={{ backgroundColor: item.color }}
                      />
                      <span>{item.name}</span>
                    </div>
                  ))}
                </div>

                {/* Small Circular Dials */}
                <div className="flex items-center justify-center gap-6 md:gap-8 mt-6">
                  {chartData.map((item) => (
                    <CircularDial
                      key={item.name}
                      progress={item.progress}
                      color={item.color}
                      name={item.name}
                    />
                  ))}
                </div>
              </div>
            </div>

            {progressBars.map((bar) => {
              const progress =
                bar.total_milestones > 0
                  ? (bar.completed_milestones / bar.total_milestones) * 100
                  : 0;
              return (
                <div
                  key={bar.id}
                  className="bg-white/90 backdrop-blur-sm rounded-2xl p-5 md:p-6 shadow-md"
                >
                  <h3 className="text-sm md:text-base font-semibold text-gray-700 mb-3">
                    {bar.title}
                  </h3>
                  <div className="space-y-2">
                    <ProgressBar
                      value={progress}
                      total={100}
                      color={bar.color}
                    />
                    <p className="text-xs md:text-sm text-gray-500">
                      {bar.completed_milestones} out of {bar.total_milestones}{" "}
                      milestones completed
                    </p>
                  </div>
                </div>
              );
            })}

            {/* Latest Achievements */}
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-5 md:p-6 shadow-md">
              <h3 className="text-sm md:text-base font-semibold text-gray-700 mb-4">Latest Achievements:</h3>
              
              {/* Achievement Cards */}
              <div className="flex gap-2 md:gap-3 overflow-x-auto pb-3 scrollbar-hide">
                {latestAchievements.map((achievement) => (
                  <AchievementCard
                    key={achievement.question_id}
                    title={achievement.title}
                    date={achievement.date}
                    description={achievement.description}
                    bgColor="bg-sky-100"
                    borderColor="border-sky-300"
                  />
                ))}
              </div>

              {/* Next Goals Section */}
              <h4 className="text-sm md:text-base font-semibold text-gray-700 mb-3 mt-5">Next Goals</h4>
              
              <div className="flex gap-2 md:gap-3 overflow-x-auto scrollbar-hide">
                {nextGoals.map((goal) => (
                  <GoalCard
                    key={goal.question_id}
                    title={goal.title}
                    date={goal.estimated_date}
                    description={goal.description}
                    tips={goal.tips}
                    bgColor="bg-pink-50"
                    borderColor="border-pink-200"
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Fixed Footer */}
        <div className="fixed inset-x-0 bottom-0 bg-white/90 backdrop-blur-sm border-t border-[#e5e7eb]">
          <div className="px-4 md:px-6 lg:px-8 py-3">
            <div className="max-w-2xl mx-auto flex items-center gap-3">
              <div className="flex-1">
                <p className="text-[11px] text-gray-500">
                  Last updated: {lastUpdated}
                </p>
                <p className="text-xs font-medium text-[#1e3a5f]">
                  Overall Progress: {Math.round(overallProgress)}%
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => {/* Add share logic */}}
                  className="px-6 py-2.5 rounded-xl bg-[#1e3a5f] text-white text-sm font-semibold shadow-md flex items-center gap-2"
                >
                  <Share2 className="w-4 h-4" />
                  Share Progress
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Circular Dial Component for Individual Progress
function CircularDial({ progress, color, name }) {
  const size = 120;
  const strokeWidth = 12;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const progressValue = Math.min(Math.max(progress, 0), 100);
  const offset = circumference - (progressValue / 100) * circumference;

  return (
    <div className="flex flex-col items-center">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="transform -rotate-90">
          {/* Background circle */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="#e5e7eb"
            strokeWidth={strokeWidth}
            fill="none"
          />
          {/* Progress circle */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke={color}
            strokeWidth={strokeWidth}
            fill="none"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            style={{ transition: 'stroke-dashoffset 0.5s ease' }}
          />
        </svg>
        
        {/* Center text */}
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-2xl font-bold text-[#102347]">
            {Math.round(progressValue)}%
          </span>
        </div>
      </div>
      <p className="text-xs font-medium text-gray-600 mt-2 text-center max-w-[100px]">
        {name}
      </p>
    </div>
  );
}

// Segmented Donut Chart Component - FIXED VERSION
function SegmentedDonutChart({ data, overallProgress }) {
  const width = 224;
  const height = 224;
  const outerRadius = 100;
  const innerRadius = 76;
  const centerX = width / 2;
  const centerY = height / 2;
  const gap = 2; // degrees between segments

  // Helper function to convert polar to cartesian
  const polarToCartesian = (centerX, centerY, radius, angleInDegrees) => {
    const angleInRadians = (angleInDegrees * Math.PI) / 180.0;
    return {
      x: centerX + radius * Math.cos(angleInRadians),
      y: centerY + radius * Math.sin(angleInRadians),
    };
  };

  // Helper function to create donut slice path
  const describeDonutSlice = (
    x,
    y,
    outerRadius,
    innerRadius,
    startAngle,
    endAngle
  ) => {
    const startOuter = polarToCartesian(x, y, outerRadius, endAngle);
    const endOuter = polarToCartesian(x, y, outerRadius, startAngle);
    const startInner = polarToCartesian(x, y, innerRadius, endAngle);
    const endInner = polarToCartesian(x, y, innerRadius, startAngle);

    const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";

    const d = [
      "M", startOuter.x, startOuter.y,
      "A", outerRadius, outerRadius, 0, largeArcFlag, 0, endOuter.x, endOuter.y,
      "L", endInner.x, endInner.y,
      "A", innerRadius, innerRadius, 0, largeArcFlag, 1, startInner.x, startInner.y,
      "Z",
    ].join(" ");

    return d;
  };

  // Calculate total progress to find proportions
  const totalProgress = data.reduce((sum, item) => sum + item.progress, 0);
  
  // Create segments based on progress proportions
  let currentAngle = -90; // Start from top
  const segments = [];
  const labels = [];

  data.forEach((item, index) => {
    // Calculate proportion of this segment based on its progress
    const proportion = totalProgress > 0 ? item.progress / totalProgress : 0;
    const sweepAngle = proportion * 360 - gap;
    
    if (sweepAngle > 0) {
      // Create fully filled segment with exact color
      segments.push({
        startAngle: currentAngle,
        endAngle: currentAngle + sweepAngle,
        color: item.color,
        name: item.name
      });
      
      // Calculate label position in the middle of this segment
      const labelAngle = currentAngle + sweepAngle / 2;
      const angleRad = labelAngle * (Math.PI / 180);
      const labelRadius = 140;
      const x = centerX + labelRadius * Math.cos(angleRad);
      const y = centerY + labelRadius * Math.sin(angleRad);
      
      // Determine text color based on background brightness
      const hex = item.color.replace("#", "");
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      const brightness = (r * 299 + g * 587 + b * 114) / 1000;
      const textColor = brightness > 128 ? "#102347" : "white";
      
      labels.push({
        x: (x / width) * 100,
        y: (y / height) * 100,
        value: Math.round(item.progress),
        bgColor: item.color,
        textColor: textColor
      });
      
      currentAngle += sweepAngle + gap;
    }
  });

  return (
    <div className="relative w-48 h-48 md:w-56 md:h-56 mb-6">
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
        {segments.map((segment, index) => (
          <path
            key={index}
            d={describeDonutSlice(
              centerX,
              centerY,
              outerRadius,
              innerRadius,
              segment.startAngle,
              segment.endAngle
            )}
            fill={segment.color}
          />
        ))}
      </svg>
      
      {/* Center text */}
      <div className="absolute inset-[24px] md:inset-[28px] rounded-full bg-white flex flex-col items-center justify-center text-center">
        <p className="text-[11px] md:text-xs font-semibold tracking-wide text-gray-700 leading-3 mb-1">
          OVERALL
          <br />
          MILESTONE
          <br />
          ACHIEVEMENT
        </p>
        <p className="text-3xl md:text-4xl font-bold text-[#102347] mt-1">
          {Math.round(overallProgress)}%
        </p>
      </div>
      
      {/* Percentage labels showing progress */}
      {labels.map((label, index) => (
        <div
          key={index}
          className="absolute text-xs font-semibold px-2 py-1 rounded-md shadow-sm"
          style={{
            backgroundColor: label.bgColor,
            color: label.textColor,
            top: `${label.y}%`,
            left: `${label.x}%`,
            transform: "translate(-50%, -50%)",
          }}
        >
          {label.value}%
        </div>
      ))}
    </div>
  );
}

// Progress Bar Component
function ProgressBar({ value, total, color }) {
  const percentage = Math.round((value / total) * 100);

  // Safelist Tailwind classes by mapping them to prevent purging
  const colorClassMap = {
    "bg-sky-400": "bg-sky-400",
    "bg-pink-400": "bg-pink-400",
    "bg-yellow-400": "bg-yellow-400",
  };

  const barColorClass = colorClassMap[color] || "bg-gray-400"; // Fallback color

  return (
    <div className="w-full">
      {/* Percentage label + tick above bar (centered) */}
      <div className="relative mb-2 h-5">
        <div className="absolute left-1/2 -translate-x-1/2 flex flex-col items-center">
          <span className="text-[11px] font-semibold text-gray-700">
            {percentage}%
          </span>
          <span className="mt-1 w-px h-3 bg-gray-300" />
        </div>
      </div>

      {/* Rounded pill bar */}
      <div className="w-full bg-gray-100 rounded-full h-4 flex items-center px-1">
        <div
          className={`h-3 rounded-full shadow-sm ${barColorClass} transition-all duration-300`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}






// Achievement Card Component with Prominent Popup
function AchievementCard({ title, date, description, bgColor, borderColor }) {
  const [showPopup, setShowPopup] = useState(false);

  return (
    <>
      <div 
        className="relative"
        onMouseEnter={() => setShowPopup(true)}
        onMouseLeave={() => setShowPopup(false)}
      >
        <div className={`min-w-[160px] md:min-w-[180px] p-3 md:p-4 mb-4 rounded-xl ${bgColor} border ${borderColor} cursor-pointer transition-all duration-300 ${
          showPopup ? 'shadow-xl z-50 relative' : 'hover:shadow-md'
        }`}>
          <h5 className="text-xs md:text-sm font-semibold text-gray-800 mb-2">{title}</h5>
          <p className="text-[10px] md:text-xs text-gray-600">Completed on {date}</p>
        </div>

        {/* Prominent Popup */}
        {showPopup && (
          <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 w-80 md:w-96 bg-white rounded-2xl shadow-2xl border-2 border-sky-200 p-6 animate-popIn">
            {/* Close indicator */}
            <div className="absolute top-3 right-3">
              <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center">
                <span className="text-gray-400 text-xs">✕</span>
              </div>
            </div>

            <div className="space-y-4">
              {/* Header with icon */}
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-2xl">🎉</span>
                </div>
                <div className="flex-1">
                  <h6 className="text-lg font-bold text-gray-900 mb-1">{title}</h6>
                  <p className="text-sm text-gray-500">Achieved on {date}</p>
                </div>
              </div>
              
              {/* Divider */}
              <div className="border-t-2 border-gray-100"></div>

              {/* Description */}
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4">
                <p className="text-sm text-gray-700 leading-relaxed">
                  🌟 {description}
                </p>
              </div>

              {/* Status and details */}
              <div className="flex items-center justify-between pt-2">
                <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold bg-green-100 text-green-800 border border-green-200">
                  ✓ Completed
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

// Goal Card Component with Prominent Popup
function GoalCard({ title, date, description, tips, bgColor, borderColor }) {
  const [showPopup, setShowPopup] = useState(false);

  return (
    <>
      <div 
        className="relative"
        onMouseEnter={() => setShowPopup(true)}
        onMouseLeave={() => setShowPopup(false)}
      >
        <div className={`min-w-[160px] md:min-w-[180px] p-3 md:p-4 mb-4 rounded-xl ${bgColor} border ${borderColor} cursor-pointer transition-all duration-300 ${
          showPopup ? 'shadow-xl z-50 relative' : 'hover:shadow-md'
        }`}>
          <h5 className="text-xs md:text-sm font-semibold text-gray-800 mb-2 leading-tight">{title}</h5>
          <p className="text-[10px] md:text-xs text-gray-600">{date}</p>
        </div>

        {/* Prominent Popup */}
        {showPopup && (
          <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 w-80 md:w-96 bg-white rounded-2xl shadow-2xl border-2 border-pink-200 p-6 animate-popIn">
            {/* Close indicator */}
            <div className="absolute top-3 right-3">
              <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center">
                <span className="text-gray-400 text-xs">✕</span>
              </div>
            </div>

            <div className="space-y-4">
              {/* Header with icon */}
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-2xl">🎯</span>
                </div>
                <div className="flex-1">
                  <h6 className="text-lg font-bold text-gray-900 mb-1">{title}</h6>
                  <p className="text-sm text-gray-500">{date}</p>
                </div>
              </div>
              
              {/* Divider */}
              <div className="border-t-2 border-gray-100"></div>

              {/* Description */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4">
                <p className="text-sm text-gray-700 leading-relaxed mb-3">
                  🚀 {description}
                </p>
                
                <div className="bg-white/80 rounded-lg p-3 mt-3 border border-blue-100">
                  <p className="text-xs font-semibold text-blue-900 mb-2 flex items-center gap-1">
                    <span>💡</span> Tips to support:
                  </p>
                  <ul className="text-xs text-blue-800 space-y-1.5 ml-4 list-disc">
                    {(tips || '').split('.').map((tip, index) => tip && <li key={index}>{tip.trim()}</li>)}
                  </ul>
                </div>
              </div>

              {/* Status and details */}
              <div className="flex items-center justify-between pt-2">
                <span className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800 border border-yellow-200">
                  ⏳ In Progress
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
